/**
 * Enhanced Sentry monitoring utilities for tracking resource loading,
 * network failures, and content blocking detection.
 */

import * as Sentry from '@sentry/vue'

/**
 * Setup global error listener for resource loading failures
 * Captures failures for: images, scripts, stylesheets, videos, PDFs, etc.
 */
export function setupResourceLoadingMonitoring() {
  window.addEventListener(
    'error',
    (event) => {
      // Only handle resource loading errors (not JavaScript errors)
      if (event.target !== window) {
        const target = event.target as HTMLElement
        const resourceUrl = (target as any).src || (target as any).href
        const resourceType = target.tagName?.toLowerCase()

        // Determine if this might be content filtering
        const isPossibleBlocking = detectContentBlocking(resourceUrl, event)

        Sentry.captureException(new Error(`Resource failed to load: ${resourceUrl}`), {
          level: 'warning',
          tags: {
            error_type: 'resource_load_failure',
            resource_type: resourceType,
            possible_blocking: isPossibleBlocking ? 'true' : 'false',
          },
          contexts: {
            resource: {
              url: resourceUrl,
              type: resourceType,
              tagName: target.tagName,
            },
          },
          fingerprint: ['resource-load-failure', resourceType, resourceUrl],
        })

        // Also log to console for local debugging
        console.error(`[Sentry] Resource load failed:`, {
          url: resourceUrl,
          type: resourceType,
          possibleBlocking: isPossibleBlocking,
        })
      }
    },
    true, // Use capture phase to catch all errors
  )
}

/**
 * Setup CSP violation monitoring
 * Detects when Content Security Policy blocks resources
 */
export function setupCSPViolationMonitoring() {
  document.addEventListener('securitypolicyviolation', (event) => {
    Sentry.captureException(new Error(`CSP Violation: ${event.violatedDirective}`), {
      level: 'warning',
      tags: {
        error_type: 'csp_violation',
        directive: event.violatedDirective,
        disposition: event.disposition,
      },
      contexts: {
        csp: {
          blockedURI: event.blockedURI,
          violatedDirective: event.violatedDirective,
          effectiveDirective: event.effectiveDirective,
          originalPolicy: event.originalPolicy,
          sourceFile: event.sourceFile,
          statusCode: event.statusCode,
          disposition: event.disposition,
        },
      },
      fingerprint: ['csp-violation', event.violatedDirective, event.blockedURI],
    })

    console.warn('[Sentry] CSP Violation:', {
      directive: event.violatedDirective,
      blockedURI: event.blockedURI,
      disposition: event.disposition,
    })
  })
}

/**
 * Detect if a resource failure might be due to content filtering/blocking
 */
function detectContentBlocking(url: string, event: Event | ErrorEvent): boolean {
  // Check for common indicators of content blocking
  const blockingIndicators = [
    // Ad blocker patterns
    /ads?[-.]|doubleclick|analytics|tracking|pixel/i.test(url),
    // Firewall/proxy patterns (no specific error message available in Event)
    false, // Can't detect from event alone
    // CORS issues might indicate proxy interference
    url.includes('cors'),
  ]

  return blockingIndicators.some(Boolean)
}

/**
 * Track download attempts and failures for PDFs and videos
 */
export interface DownloadAttempt {
  resourceType: 'pdf' | 'video' | 'other'
  url: string
  lessonId?: number
  lessonToken?: string
  startTime: number
}

const activeDownloads = new Map<string, DownloadAttempt>()

/**
 * Start tracking a download attempt
 */
export function trackDownloadStart(attempt: DownloadAttempt) {
  activeDownloads.set(attempt.url, attempt)

  Sentry.addBreadcrumb({
    category: 'download',
    message: `Download started: ${attempt.resourceType}`,
    level: 'info',
    data: {
      url: attempt.url,
      resourceType: attempt.resourceType,
      lessonId: attempt.lessonId,
      lessonToken: attempt.lessonToken,
    },
  })

  // Set a timeout to detect hanging downloads
  const timeoutId = setTimeout(() => {
    if (activeDownloads.has(attempt.url)) {
      trackDownloadTimeout(attempt)
    }
  }, 60000) // 60 second timeout

  // Store timeout ID for cleanup
  ;(attempt as any)._timeoutId = timeoutId
}

/**
 * Mark a download as successful
 */
export function trackDownloadSuccess(url: string) {
  const attempt = activeDownloads.get(url)
  if (!attempt) return

  const duration = Date.now() - attempt.startTime

  Sentry.addBreadcrumb({
    category: 'download',
    message: `Download succeeded: ${attempt.resourceType}`,
    level: 'info',
    data: {
      url,
      resourceType: attempt.resourceType,
      duration,
      lessonId: attempt.lessonId,
      lessonToken: attempt.lessonToken,
    },
  })

  // Clear timeout
  clearTimeout((attempt as any)._timeoutId)
  activeDownloads.delete(url)
}

/**
 * Track a download failure
 */
export function trackDownloadFailure(url: string, error: Error | string) {
  const attempt = activeDownloads.get(url)
  const duration = attempt ? Date.now() - attempt.startTime : 0

  const errorMessage = typeof error === 'string' ? error : error.message
  const isPossibleBlocking =
    errorMessage.includes('Network Error') ||
    errorMessage.includes('Failed to fetch') ||
    errorMessage.includes('ERR_BLOCKED_BY_CLIENT') ||
    errorMessage.includes('net::ERR')

  Sentry.captureException(
    typeof error === 'string' ? new Error(error) : error,
    {
      level: 'error',
      tags: {
        error_type: 'download_failure',
        resource_type: attempt?.resourceType || 'unknown',
        possible_blocking: isPossibleBlocking ? 'true' : 'false',
        lesson_id: attempt?.lessonId?.toString(),
        lesson_token: attempt?.lessonToken,
      },
      contexts: {
        download: {
          url,
          resourceType: attempt?.resourceType,
          duration,
          lessonId: attempt?.lessonId,
          lessonToken: attempt?.lessonToken,
        },
      },
      fingerprint: ['download-failure', attempt?.resourceType || 'unknown', url],
    },
  )

  // Clear timeout
  if (attempt) {
    clearTimeout((attempt as any)._timeoutId)
  }
  activeDownloads.delete(url)
}

/**
 * Track a download timeout
 */
function trackDownloadTimeout(attempt: DownloadAttempt) {
  const duration = Date.now() - attempt.startTime

  Sentry.captureException(new Error(`Download timeout: ${attempt.url}`), {
    level: 'warning',
    tags: {
      error_type: 'download_timeout',
      resource_type: attempt.resourceType,
      possible_blocking: 'true', // Timeouts often indicate filtering
      lesson_id: attempt.lessonId?.toString(),
      lesson_token: attempt.lessonToken,
    },
    contexts: {
      download: {
        url: attempt.url,
        resourceType: attempt.resourceType,
        duration,
        lessonId: attempt.lessonId,
        lessonToken: attempt.lessonToken,
      },
    },
    fingerprint: ['download-timeout', attempt.resourceType, attempt.url],
  })

  activeDownloads.delete(attempt.url)
}

/**
 * Initialize all monitoring
 */
export function initializeSentryMonitoring() {
  setupResourceLoadingMonitoring()
  setupCSPViolationMonitoring()

  console.log('[Sentry] Enhanced monitoring initialized')
}
